// Builds the static level geometry, lighting rig and lounge decor.
import { GeoBuilder, m } from '../engine/geometry.js';
import { Node, Light } from '../engine/renderer.js';
import { col } from '../engine/math.js';
import * as M from './models.js';
import { COUNTER_H } from './stations.js';

const WALKABLE = '.1234@&=';

export function buildWorld(game) {
  const { level, theme, scene } = game;
  const map = level.map;
  const H = map.length, W = map[0].length;
  const at = (x, y) => (y >= 0 && y < H && x >= 0 && x < W ? map[y][x] : ' ');
  const tw = (x, y) => game.tileToWorld(x, y);
  const b = new GeoBuilder();
  const deco = new GeoBuilder();

  const floorM = m(shadeHex(theme.floor, 1.9), { rough: 0.24, refl: 0.5 });
  const floorM2 = m(shadeHex(theme.floor, 2.3), { rough: 0.28, refl: 0.45 });
  const lineM = m(theme.accent2, { rough: 0.3, emit: 0.9 });
  const counterTop = m(shadeHex(theme.counter, 0.78), { rough: 0.2 });
  const counterTop2 = m(shadeHex(theme.counter, 0.72), { rough: 0.2 });
  const counterInset = m(shadeHex(theme.counter, 0.62), { rough: 0.35 });
  const counterSide = m(shadeHex(theme.floor, 1.6), { rough: 0.4, metal: 0.2 });
  const trimM = m(theme.accent, { rough: 0.3, emit: 2.2 });
  const woodM = m(theme.wood, { rough: 0.45 });
  const goldM = m('#ffcf7a', { rough: 0.25, metal: 1 });
  const carpetM = m(shadeHex(theme.neb1, 0.9), { rough: 0.95 });
  const loungeFloor = m(shadeHex(theme.floor, 0.8), { rough: 0.3, refl: 0.4 });

  // --- saucer deck (big disc under everything)
  const R = Math.max(W, H) * 0.72 + 3;
  b.cylinder(R, R - 0.6, 0.6, m('#2a2a3a', { rough: 0.35, metal: 0.8 }), { p: [0, -0.32, 0] }, 64, false);
  b.disc(R, m(shadeHex(theme.floor, 0.6), { rough: 0.3, metal: 0.5, refl: 0.3 }), { p: [0, -0.03, 0] }, 64);
  b.torus(R, 0.08, m('#8a8aa8', { rough: 0.2, metal: 1 }), { p: [0, -0.03, 0] }, 96, 6);
  for (let i = 0; i < 48; i++) {
    const a = (i / 48) * Math.PI * 2;
    deco.sphere(0.07, m(i % 2 ? theme.accent : theme.accent2, { emit: 5 }), { p: [Math.cos(a) * (R - 0.02), 0.0, Math.sin(a) * (R - 0.02)] }, 8, 6);
  }
  // decorative concentric rings on the deck
  for (const rr of [R - 1.2, R - 2.2]) b.torus(rr, 0.025, m(theme.accent2, { emit: 1.2 }), { p: [0, -0.02, 0] }, 96, 4);

  // --- tiles
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const c = at(x, y);
      const [wx, wz] = tw(x, y);
      if (c === ' ' || c === '=') continue;
      if (c === '~' || c === 'g') {
        b.box(1, 0.06, 1, loungeFloor, { p: [wx, -0.0, wz] });
        continue;
      }
      // kitchen floor under everything (not void)
      b.box(1.0, 0.04, 1.0, lineM, { p: [wx, -0.015, wz] });
      b.roundBox(0.965, 0.06, 0.965, 0.02, (x + y) % 2 ? floorM : floorM2, { p: [wx, 0.0, wz] }, 1);
      if (WALKABLE.includes(c)) continue;
      if (c === 'W') {
        // planter wall
        b.roundBox(0.96, 1.3, 0.96, 0.1, m('#2a2438', { rough: 0.35, metal: 0.4 }), { p: [wx, 0.65, wz] });
        b.box(0.8, 0.06, 0.8, m('#3a2a1a', { rough: 1 }), { p: [wx, 1.3, wz] });
        deco.merge(plantBuilder(theme), { p: [wx, 1.28, wz], s: 0.8 });
        continue;
      }
      if (c === '>' || c === '<' || c === '^' || c === '|') {
        b.roundBox(0.98, COUNTER_H - 0.1, 0.98, 0.05, counterSide, { p: [wx, (COUNTER_H - 0.1) / 2, wz] });
        continue;
      }
      // counter body
      const isBar = c === 'T';
      b.roundBox(0.98, COUNTER_H - 0.06, 0.98, 0.04, isBar ? woodM : counterSide, { p: [wx, (COUNTER_H - 0.06) / 2, wz] });
      b.roundBox(1.0, 0.07, 1.0, 0.025, isBar ? m('#e8dccb', { rough: 0.12 }) : (x + y) % 2 ? counterTop : counterTop2, { p: [wx, COUNTER_H - 0.035, wz] }, 2);
      if (!isBar) b.roundBox(0.84, 0.012, 0.84, 0.005, counterInset, { p: [wx, COUNTER_H + 0.001, wz] }, 1);
      if (isBar) {
        b.box(1.0, 0.02, 0.02, goldM, { p: [wx, COUNTER_H - 0.07, wz + 0.5] });
        b.box(1.0, 0.02, 0.02, goldM, { p: [wx, COUNTER_H - 0.07, wz - 0.5] });
        b.box(1.0, 0.03, 0.02, m(theme.accent2, { emit: 2.5 }), { p: [wx, 0.12, wz - 0.5] });
      }
      // neon trim facing walkable tiles
      for (const [dx, dy] of [[0, 1], [0, -1], [1, 0], [-1, 0]]) {
        if (!WALKABLE.includes(at(x + dx, y + dy))) continue;
        const px = wx + dx * 0.5, pz = wz + dy * 0.5;
        const horiz = dy !== 0;
        b.box(horiz ? 0.9 : 0.02, 0.025, horiz ? 0.02 : 0.9, trimM, { p: [px, 0.1, pz] });
        b.box(horiz ? 0.98 : 0.015, 0.012, horiz ? 0.015 : 0.98, goldM, { p: [px, COUNTER_H - 0.075, pz] });
      }
    }

  // --- lounge (north of row 0): floor extension, carpet, tables, windows
  const [lx0, lz0] = tw(0, 0);
  const loungeZ = lz0;
  const x0 = tw(0, 0)[0] - 0.5, x1 = tw(W - 1, 0)[0] + 0.5;
  const lw = x1 - x0;
  b.box(lw + 2, 0.06, 3.2, loungeFloor, { p: [(x0 + x1) / 2, -0.0, loungeZ - 2.1] });
  b.box(lw - 0.4, 0.02, 1.6, carpetM, { p: [(x0 + x1) / 2, 0.035, loungeZ - 1.9] });
  b.box(lw - 0.2, 0.01, 0.04, m(theme.accent, { emit: 2 }), { p: [(x0 + x1) / 2, 0.04, loungeZ - 1.1] });
  // window wall arcs at the back
  const backZ = loungeZ - 3.7;
  const frameM = m('#1c1a28', { rough: 0.3, metal: 0.8 });
  b.box(lw + 2, 0.35, 0.3, frameM, { p: [(x0 + x1) / 2, 0.17, backZ] });
  b.box(lw + 2, 0.04, 0.05, m(theme.accent2, { emit: 3 }), { p: [(x0 + x1) / 2, 0.36, backZ + 0.13] });
  const nPanes = Math.max(3, Math.round(lw / 2.6));
  for (let i = 0; i <= nPanes; i++) {
    const px = x0 - 1 + ((lw + 2) * i) / nPanes;
    b.box(0.14, 4.2, 0.2, frameM, { p: [px, 2.1, backZ] });
    b.box(0.03, 4.0, 0.03, m(theme.accent, { emit: 2.2 }), { p: [px, 2.1, backZ + 0.1] });
  }
  // glass
  const glass = new GeoBuilder();
  glass.box(lw + 2, 4.2, 0.02, m('#9fd8ff'), { p: [(x0 + x1) / 2, 2.1, backZ] });
  const glassNode = new Node(M.meshFrom(glass), { fx: { mode: 0 }, castShadow: false, reflect: false });
  glassNode.tint = [0.02, 0.03, 0.05, 1];
  scene.add(glassNode);

  // side walls (west/east) low with portholes
  const zTop = backZ, zBot = tw(0, H - 1)[1] + 0.5;
  for (const side of [-1, 1]) {
    const sx = side < 0 ? x0 - 1.0 : x1 + 1.0;
    const len = zBot - zTop;
    b.box(0.3, 1.4, len, frameM, { p: [sx, 0.7, (zTop + zBot) / 2] });
    b.box(0.05, 0.04, len, m(theme.accent2, { emit: 2.5 }), { p: [sx - side * 0.16, 1.2, (zTop + zBot) / 2] });
    for (let z = zTop + 1.5; z < zBot - 0.5; z += 2.2) {
      b.torus(0.32, 0.06, goldM, { p: [sx - side * 0.16, 0.72, z], r: [0, 0, Math.PI / 2] }, 20, 6);
      b.disc(0.3, m('#0a0a20', { rough: 0.05, emit: 0.2 }), { p: [sx - side * 0.155, 0.72, z], r: [0, 0, side * Math.PI / 2] }, 18);
    }
  }

  // seats & lounge decor
  const seats = [];
  for (let x = 0; x < W; x++) if (at(x, 0) === 'g') seats.push(x);
  for (const sx of seats) {
    const [wx, wz] = tw(sx, 0);
    deco.merge(stoolBuilder(theme), { p: [wx, 0, wz - 0.05] });
  }
  // tables with teapots between seats, plants on ends
  for (let x = 0; x < W; x++) {
    if (at(x, 0) !== '~') continue;
    const [wx, wz] = tw(x, 0);
    if (x === 0 || x === W - 1) deco.merge(plantBuilder(theme), { p: [wx, 0, wz - 0.6] });
  }
  for (let i = 0; i < nPanes; i++) {
    const px = x0 - 1 + ((lw + 2) * (i + 0.5)) / nPanes;
    deco.merge(tableBuilder(theme), { p: [px, 0, loungeZ - 2.5] });
    deco.merge(teapotBuilder(), { p: [px, 0.74, loungeZ - 2.5], r: [0, i, 0] });
    deco.merge(plantBuilder(theme), { p: [x0 - 1 + ((lw + 2) * i) / nPanes + 0.01, 0, backZ + 0.5] });
  }

  const staticNode = new Node(M.meshFrom(b));
  scene.add(staticNode);
  const decoNode = new Node(M.meshFrom(deco));
  scene.add(decoNode);

  // --- lights
  const lights = { lamps: [], shafts: [], all: [] };
  const addL = (o) => {
    const l = new Light(o);
    scene.lights.push(l);
    lights.all.push(l);
    return l;
  };
  // pendant lamps over the bar
  const lampY = 2.9;
  const barLamps = Math.max(2, Math.round(lw / 3));
  for (let i = 0; i < barLamps; i++) {
    const px = x0 + (lw * (i + 0.5)) / barLamps;
    const pz = loungeZ - 0.2;
    const n = new Node(M.lampMesh(theme));
    n.position = [px, lampY, pz];
    scene.add(n);
    const l = addL({ position: [px, lampY - 0.2, pz], color: '#ffd6a0', intensity: 9, range: 6.5, dir: [0, -1, 0], cone: 1.0, soft: 0.6 });
    lights.lamps.push({ node: n, light: l, base: 9, phase: i });
    const shaft = new Node(M.coneBeamMesh(0.22, 1.1, lampY), { fx: { mode: 2, param: lampY }, castShadow: false, reflect: false });
    shaft.position = [px, lampY / 2 - 0.02, pz];
    shaft.tint = [1.0, 0.8, 0.55, 0.05];
    scene.add(shaft);
    lights.shafts.push(shaft);
  }
  // starlight shafts pouring in through the big window
  for (let i = 0; i < 3; i++) {
    const px = x0 + lw * (0.2 + i * 0.3);
    const shaft = new Node(M.coneBeamMesh(0.5, 1.5, 7), { fx: { mode: 2, param: 7 }, castShadow: false, reflect: false });
    shaft.position = [px, 2.6, backZ + 2.6];
    shaft.rotation = [-1.05, 0, 0.12 * (i - 1)];
    shaft.tint = [...col(i === 1 ? theme.accent2 : '#cfd8ff'), 0.035];
    shaft.userData.a = 0.035;
    scene.add(shaft);
    lights.shafts.push(shaft);
  }
  // kitchen fill lights (overhead, invisible)
  const [cx0, cz0] = tw(0, 1), [cx1, cz1] = tw(W - 1, H - 1);
  const nx = Math.max(2, Math.round(W / 5)), nz = Math.max(1, Math.round(H / 4));
  for (let i = 0; i < nx; i++)
    for (let j = 0; j < nz; j++) {
      const px = cx0 + ((cx1 - cx0) * (i + 0.5)) / nx, pz = cz0 + ((cz1 - cz0) * (j + 0.5)) / nz + 0.5;
      addL({ position: [px, 3.6, pz], color: j % 2 ? theme.accent2 : '#fff0e0', intensity: 7, range: 7.5 });
    }
  // accent uplights at the window
  for (let i = 0; i <= nPanes; i++) {
    const px = x0 - 1 + ((lw + 2) * i) / nPanes;
    addL({ position: [px, 0.4, backZ + 0.3], color: i % 2 ? theme.accent : theme.accent2, intensity: 3.5, range: 3 });
  }
  // plants glow on ends
  addL({ position: [x0 - 0.5, 1, lz0 - 0.5], color: theme.accent2, intensity: 2, range: 3 });
  addL({ position: [x1 + 0.5, 1, lz0 - 0.5], color: theme.accent, intensity: 2, range: 3 });

  // teleporter pads & bridges
  const teleports = [];
  const bridges = [];
  for (let y = 0; y < H; y++)
    for (let x = 0; x < W; x++) {
      const c = at(x, y);
      const [wx, wz] = tw(x, y);
      if (c === '@' || c === '&') {
        const n = new Node(M.stationMesh('teleport', theme));
        n.position = [wx, 0.02, wz];
        scene.add(n);
        const beam = new Node(M.beamMesh(0.42, 2.2), { fx: { mode: 2, param: 2.2 }, castShadow: false });
        beam.position = [wx, 1.12, wz];
        beam.tint = [...col(theme.accent2), 0.25];
        beam.rotation = [Math.PI, 0, 0];
        scene.add(beam);
        const l = addL({ position: [wx, 0.6, wz], color: theme.accent2, intensity: 2.5, range: 2.5 });
        teleports.push({ x, y, ch: c, wx, wz, beam, light: l, pad: n });
      }
      if (c === '=') {
        const bb = new GeoBuilder();
        bb.roundBox(0.96, 0.08, 0.96, 0.02, m('#3a3a50', { rough: 0.3, metal: 0.8, refl: 0.3 }), { p: [0, 0, 0] });
        bb.box(0.9, 0.012, 0.04, m(theme.accent, { emit: 3 }), { p: [0, 0.045, 0.4] });
        bb.box(0.9, 0.012, 0.04, m(theme.accent, { emit: 3 }), { p: [0, 0.045, -0.4] });
        const n = new Node(M.meshFrom(bb));
        n.position = [wx, 0, wz];
        scene.add(n);
        bridges.push({ x, y, wx, wz, node: n, ext: 1, homeX: wx, side: x < W / 2 ? -1 : 1 });
      }
    }

  return { staticNode, decoNode, lights, teleports, bridges, seats, loungeZ, backZ, bounds: { x0, x1, zTop, zBot, R } };
}

function stoolBuilder(theme) {
  const b = new GeoBuilder();
  b.cylinder(0.05, 0.05, 0.5, m('#e8ecf5', { rough: 0.12, metal: 1 }), { p: [0, 0.25, 0] }, 8);
  b.cylinder(0.2, 0.22, 0.03, m('#e8ecf5', { rough: 0.12, metal: 1 }), { p: [0, 0.015, 0] }, 16);
  b.cylinder(0.24, 0.22, 0.08, m(theme.accent2, { rough: 0.6 }), { p: [0, 0.52, 0] }, 18);
  b.torus(0.23, 0.012, m(theme.accent2, { emit: 2 }), { p: [0, 0.48, 0] }, 20, 4);
  return b;
}
function plantBuilder(theme) {
  const b = new GeoBuilder();
  b.lathe([[0.001, 0], [0.2, 0], [0.26, 0.4], [0.24, 0.42], [0.001, 0.38]], 18, m('#2a2438', { rough: 0.4, metal: 0.3 }));
  b.torus(0.255, 0.012, m(theme.accent, { emit: 2 }), { p: [0, 0.4, 0] }, 20, 4);
  for (let i = 0; i < 9; i++) {
    const a = i * 0.7;
    b.leaf(0.3, 0.075, m(i % 2 ? '#2a8a6a' : '#3ab07a', { rough: 0.5 }), { p: [Math.cos(a) * 0.1, 0.62 + (i % 3) * 0.08, Math.sin(a) * 0.1], r: [-0.9 + (i % 3) * 0.2, a, 0] });
  }
  for (let i = 0; i < 4; i++) b.sphere(0.045, m(i % 2 ? theme.accent : theme.accent2, { emit: 5 }), { p: [Math.cos(i * 1.6) * 0.18, 0.85 + (i % 2) * 0.15, Math.sin(i * 1.6) * 0.18] }, 8, 6);
  return b;
}
function tableBuilder(theme) {
  const b = new GeoBuilder();
  b.cylinder(0.34, 0.34, 0.04, m('#f5efe6', { rough: 0.15 }), { p: [0, 0.72, 0] }, 24);
  b.torus(0.34, 0.015, m('#ffcf7a', { rough: 0.25, metal: 1 }), { p: [0, 0.72, 0] }, 24, 4);
  b.cylinder(0.04, 0.04, 0.7, m('#e8ecf5', { rough: 0.12, metal: 1 }), { p: [0, 0.36, 0] }, 8);
  b.cylinder(0.18, 0.2, 0.03, m('#e8ecf5', { rough: 0.12, metal: 1 }), { p: [0, 0.015, 0] }, 16);
  for (const s of [-1, 1]) {
    b.cylinder(0.2, 0.2, 0.06, m(theme.accent2, { rough: 0.6 }), { p: [s * 0.55, 0.45, 0] }, 16);
    b.cylinder(0.03, 0.03, 0.44, m('#e8ecf5', { rough: 0.12, metal: 1 }), { p: [s * 0.55, 0.22, 0] }, 6);
  }
  return b;
}
function teapotBuilder() {
  const b = new GeoBuilder();
  const cer = m('#fbf8ff', { rough: 0.18 });
  b.lathe([[0.001, 0], [0.1, 0], [0.14, 0.06], [0.13, 0.13], [0.07, 0.18], [0.001, 0.19]], 16, cer);
  b.sphere(0.02, m('#ffcf7a', { rough: 0.25, metal: 1 }), { p: [0, 0.2, 0] });
  b.cylinder(0.012, 0.02, 0.12, cer, { p: [0.15, 0.1, 0], r: [0, 0, -0.9] }, 6);
  b.torus(0.05, 0.012, cer, { p: [-0.14, 0.1, 0], r: [Math.PI / 2, 0, 0] }, 10, 4, Math.PI * 2);
  for (const s of [-1, 1]) b.lathe([[0.001, 0], [0.05, 0], [0.06, 0.06], [0.05, 0.06], [0.001, 0.01]], 12, cer, { p: [s * 0.2, 0, 0.12] });
  return b;
}

export function shadeHex(hex, k) {
  const c = parseInt(hex.slice(1), 16);
  const f = (v) => Math.max(0, Math.min(255, Math.round(v * k)));
  const r = f((c >> 16) & 255), g = f((c >> 8) & 255), bb = f(c & 255);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + bb).toString(16).slice(1);
}
